import { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import ContestTable2 from "../components/ContestTable2";
import { API_BASE } from "../utils/Instance";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export default function AllContestsHistory() {
  const [allJoinedContests, setAllJoinedContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const { profile, refreshAccessToken } = useAuth();
  const navigate = useNavigate();

  // Helper function to make authenticated API calls
  const authenticatedFetch = async (url, options = {}) => {
    const token = profile?.accessToken;
    
    if (!token) {
      navigate("/login");
      return null;
    }

    const headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };

    let response = await fetch(url, { ...options, headers });

    // If 401, try to refresh token and retry
    if (response.status === 401) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        headers.Authorization = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
      } else {
        navigate("/login");
        return null;
      }
    }

    return response;
  };

  // Fetch all contests (not just joined ones)
  const fetchAllContests = async () => {
    try {
      setLoading(true);
      const contestsResponse = await authenticatedFetch(`${API_BASE}accounts/contests/?t=${Date.now()}`);
      
      if (contestsResponse && contestsResponse.ok) {
        const contestsData = await contestsResponse.json();
        console.log('All contests:', contestsData);
        setAllJoinedContests(contestsData);
      }
    } catch (error) {
      console.error('Error fetching contests:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profile?.accessToken) {
      fetchAllContests();
    }
  }, [profile?.accessToken]);

  // Refresh data when the page gains focus (user comes back to this tab)
  useEffect(() => {
    const handleFocus = () => {
      if (profile?.accessToken) {
        console.log('Page focused - refreshing contests...');
        fetchAllContests();
      }
    };

    window.addEventListener('focus', handleFocus);
    
    // Also refresh when the component mounts or becomes visible
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && profile?.accessToken) {
        console.log('Page visible - refreshing contests...');
        fetchAllContests();
      }
    });

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [profile?.accessToken]);

  return (
    <div className="flex min-h-screen bg-black">
      <Sidebar />
      <div className="flex-1 p-4 md:p-6 lg:ml-64 w-full">
        <Topbar pageTitle="All Contests History" />

        {/* All Contests Section */}
        <div className="py-6 md:py-10">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold font-inter text-[#CEB46A]">
              All Contests Results
            </h2>
            <button
              onClick={fetchAllContests}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold
                         bg-[#ceb46a] text-black
                         hover:bg-[#e3c97a] 
                         transition duration-200 shadow-md
                         hover:shadow-lg active:scale-95
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
          {loading ? (
            <div className="text-center text-white py-10">Loading contests...</div>
          ) : allJoinedContests.length > 0 ? (
            <ContestTable2
              data={allJoinedContests.map((c) => ({
                id: c.id,
                title: c.title || c.name || "N/A",
                name: c.title || c.name || "N/A",
                gallery: c.category || "N/A",
                end_time: c.end_time,
              }))}
              showViewAllButton={false}
            />
          ) : (
            <div className="text-center text-gray-400 py-10">
              No contests available yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

